import { useCallback, useEffect, useRef } from "react";

import { datadogRum } from "@datadog/browser-rum";

import { getAppVersion } from "utils/lazy-with-retry.util";
import { plainReloadPage, reloadPage } from "utils/reload-page.util";

const RELOAD_VERIFY_STORAGE_KEY = "suno_stale_build_reload_verify";

// 0 = plain reload, 1 = cache-busted document navigation, then give up. Separate from
// (and does not share a key with) the DEV-2147 chunk-reload budget in
// lazy-with-retry.util.ts — that one is keyed per chunk name and guards a completely
// different failure (a chunk 404ing after deploy); this one is keyed per stale-build
// event and guards a reload that didn't actually pick up the new build (SN-13694).
const MAX_RELOAD_ATTEMPTS = 1;

interface ReloadVerifyRecord {
  targetSha: string;
  attempt: number;
  /** pathname + search at the moment of click, WITHOUT the escalation's own cache-bust param. */
  path: string;
  scrollY: number;
}

function readRecord(): ReloadVerifyRecord | null {
  try {
    const raw = localStorage.getItem(RELOAD_VERIFY_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ReloadVerifyRecord) : null;
  } catch {
    return null;
  }
}

function writeRecord(record: ReloadVerifyRecord): boolean {
  try {
    localStorage.setItem(RELOAD_VERIFY_STORAGE_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

function clearRecord() {
  try {
    localStorage.removeItem(RELOAD_VERIFY_STORAGE_KEY);
  } catch {
    // ignore — worst case a stale record is checked once more on the next boot
  }
}

interface UseStaleBuildReloadArgs {
  latestSha: string | null;
  /** Called once verification exhausts its attempts — suppresses the pill for this sha. */
  dismiss: () => void;
}

/**
 * ENG-747 Phase A. `/index.html` is already served `no-cache, no-store, must-revalidate`
 * on this site (netlify.toml), so a plain reload should almost always pick up the new
 * build — but "should" isn't a guarantee an intermediate proxy or the browser's own
 * cache honors, and SN-13694 ("stuck on 'We have a new version'") is what happens when
 * nothing checks. On every boot, this hook checks for a reload it triggered: if
 * `__APP_VERSION__` now matches what was promised, it restores scroll position and the
 * clean URL and stops. If not, it escalates once to a cache-busted document navigation;
 * if that also fails, it reports to RUM and dismisses the pill rather than trapping the
 * user in a reload loop.
 *
 * Deliberately does NOT gate the click on unsaved-form state (`useNavigationAwayPrompt`).
 * That context is only provided inside specific routed feature trees (patients, settings,
 * marketing, …) — `layout-top-bar`, where the pill lives, is a sibling of the router
 * `Outlet`, not a descendant of any of them, so the hook would always see a null context
 * and silently never fire. Wiring it here would be a guard that looks real in code review
 * and never actually guards anything. There is no automatic reload anywhere in this
 * design — every reload this hook performs is downstream of the user's own click — which
 * satisfies "never auto-anything" by construction; a real per-form-dirty guard would need
 * `AwayConfirmationProvider` lifted app-wide, which is a separate, larger change.
 */
export default function useStaleBuildReload({
  latestSha,
  dismiss
}: UseStaleBuildReloadArgs) {
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  useEffect(() => {
    const record = readRecord();
    if (!record) return;

    const currentSha = getAppVersion();
    if (currentSha === record.targetSha) {
      requestAnimationFrame(() => window.scrollTo(0, record.scrollY));
      if (window.location.pathname + window.location.search !== record.path) {
        window.history.replaceState(null, "", record.path);
      }
      clearRecord();
      return;
    }

    if (record.attempt >= MAX_RELOAD_ATTEMPTS) {
      datadogRum.addAction("stale_build_reload_verification_failed", {
        targetSha: record.targetSha,
        attempts: record.attempt
      });
      clearRecord();
      dismissRef.current();
      return;
    }

    if (!writeRecord({ ...record, attempt: record.attempt + 1 })) {
      clearRecord();
      return;
    }
    // Reuses the same cache-busted reload lazy-with-retry.util.ts uses for chunk-load
    // recovery — same mechanism, same reason (force a genuinely new document fetch).
    reloadPage();
    // Runs once on mount only — this checks for a PRIOR reload's outcome, it must not
    // re-run as component state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReloadClick = useCallback(() => {
    if (!latestSha) return;
    const record: ReloadVerifyRecord = {
      targetSha: latestSha,
      attempt: 0,
      path: window.location.pathname + window.location.search,
      scrollY: window.scrollY
    };
    // A write failure just means no post-reload verification — the reload still
    // happens, which is exactly today's (pre-Phase-A) behavior, not a new failure mode.
    writeRecord(record);
    plainReloadPage();
  }, [latestSha]);

  return { handleReloadClick };
}
